<template>
    <StepShell :embedded="embedded" tag="form" width="narrow" gap="none" @submit.prevent="handleSubmit">
        <!-- Centre through the wrapper to avoid styling BasePageHeader internals. Embedded setup appears
             beside a rail, so it uses normal document flow.
             -->
        <div class="heading" :class="{ 'is-centred': !embedded }">
            <BasePageHeader v-if="returning" :variant="embedded ? 'step' : 'page'" title="What should I know about you?" no-border />
            <template v-else>
                <BasePageHeader :variant="embedded ? 'step' : 'page'" title="Meet Nadia," subtitle="your new personalized coach and thought partner" no-border subtitle-below />
                <p class="intro">{{ introCopy }}</p>
            </template>
        </div>

        <div class="fields">
            <div v-for="field in FIELDS" :key="field.key" class="field" :class="{ 'role-field': field.key === 'role' }">
                <FormElement :title="field.label" :for="`identity-${field.key}`">
                    <input :id="`identity-${field.key}`" v-model="values[field.key]" class="field-input" type="text" :autocomplete="field.autocomplete" :name="field.key" />
                </FormElement>
            </div>
        </div>

        <fieldset class="manages">
            <legend id="manages-legend" class="manages-legend">Do you manage people?</legend>

            <div class="manages-options" role="radiogroup" aria-labelledby="manages-legend">
                <button
                    v-for="(option, index) in MANAGES_OPTIONS"
                    :key="option.value"
                    :ref="(el) => setOptionRef(el, index)"
                    type="button"
                    role="radio"
                    :value="option.value"
                    class="option"
                    :class="{ 'is-selected': managesChoice === option.value }"
                    :aria-checked="managesChoice === option.value"
                    :tabindex="tabStop(index) ? 0 : -1"
                    @click="managesChoice = option.value"
                    @keydown="onOptionKeydown($event, index)">
                    <span class="option-head">
                        <i aria-hidden="true" class="bi option-icon" :class="option.icon"></i>
                        <span class="option-label">{{ option.label }}</span>
                        <i v-if="managesChoice === option.value" aria-hidden="true" class="bi bi-check-lg option-check"></i>
                    </span>
                    <span class="option-body">{{ option.description }}</span>
                </button>
            </div>
        </fieldset>

        <StepActions>
            <BaseButton type="submit" theme="primary" :disabled="!ready || busy">
                {{ ctaLabel }}
            </BaseButton>
        </StepActions>
    </StepShell>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import FormElement from "~vue/components/form/FormElement.vue";
import { type ComponentPublicInstance, computed, reactive, ref } from "vue";

import StepActions from "./StepActions.vue";
import StepShell from "./StepShell.vue";
import type { IdentityPrefill } from "./types";

const {
    embedded = false,
    prefill,
    busy,
    returning = false,
    initialManagesPeople = null,
} = defineProps<{
    embedded?: boolean;
    prefill: IdentityPrefill;
    busy: boolean;
    /** Set when the user is already inside the product, so the screen stops introducing Nadia. */
    returning?: boolean;
    initialManagesPeople?: boolean | null;
}>();

const emit = defineEmits<{ submit: [{ firstName: string; lastName: string; role: string; managesPeople: boolean }] }>();

type FieldKey = "firstName" | "lastName" | "role";

const FIELDS: { key: FieldKey; label: string; autocomplete: string }[] = [
    { key: "firstName", label: "First name", autocomplete: "given-name" },
    { key: "lastName", label: "Last name", autocomplete: "family-name" },
    { key: "role", label: "Role", autocomplete: "organization-title" },
];

/*
 * A real radio group rather than a switch. A switch would default to off and
 * quietly record "does not manage people" for anyone who skipped it, and that
 * answer changes the coaching enough that we should hear it explicitly.
 */
type ManagesKey = "ic" | "manager";

const MANAGES_OPTIONS: { value: ManagesKey; icon: string; label: string; description: string }[] = [
    {
        value: "ic",
        icon: "bi-person",
        label: "I don’t, currently",
        description: "Coaching stays focused on your own work and how you land it.",
    },
    {
        value: "manager",
        icon: "bi-people",
        label: "Yes, I manage people",
        description: "You’ll also get coaching on the people who report to you.",
    },
];

/* Prefilled from the directory — the point of this screen is confirming, not typing. */
const values = reactive<Record<FieldKey, string>>({
    firstName: prefill.first_name,
    lastName: prefill.last_name,
    role: prefill.role,
});

const managesChoice = ref<ManagesKey | undefined>(initialManagesPeople === null ? undefined : initialManagesPeople ? "manager" : "ic");
const managesPeople = computed(() => (managesChoice.value === undefined ? null : managesChoice.value === "manager"));

const optionRefs = ref<(HTMLButtonElement | null)[]>([]);

function setOptionRef(el: Element | ComponentPublicInstance | null, index: number): void {
    optionRefs.value[index] = el as HTMLButtonElement | null;
}

const ARROW_STEPS: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

function tabStop(index: number): boolean {
    const checked = MANAGES_OPTIONS.findIndex((option) => option.value === managesChoice.value);
    return index === (checked === -1 ? 0 : checked);
}

function onOptionKeydown(event: KeyboardEvent, index: number): void {
    const step = ARROW_STEPS[event.key] ?? 0;
    if (step === 0) {
        return;
    }
    event.preventDefault();
    const next = (index + step + MANAGES_OPTIONS.length) % MANAGES_OPTIONS.length;
    managesChoice.value = MANAGES_OPTIONS[next].value;
    optionRefs.value[next]?.focus();
}

/*
 * Only a first name and the manages-people answer are required. This screen exists
 * to confirm what the directory already told us, and blocking on a surname or a
 * title -- neither of which every directory carries -- turns it back into a form.
 */
const hasFirstName = computed(() => values.firstName.trim().length > 0);
const ready = computed(() => hasFirstName.value && managesPeople.value !== null);

/*
 * The directory does not always carry a name, so on a cold account every field is
 * blank. A CTA that just says "Next" and does nothing is a dead end on the first
 * screen of onboarding, so the label names whichever answer is still missing.
 */
const ctaLabel = computed(() => {
    if (busy) {
        return "Setting things up…";
    }
    if (!hasFirstName.value) {
        return "Add your first name to continue";
    }
    return managesPeople.value === null ? "Pick one to continue" : "Next";
});

/* Nothing came back from the directory, so there is nothing to "correct" -- asking
   them to check blank fields reads as a bug. */
/*
 * Keep one source attribution: previous field-level notices named a different system from the introduction.
 */
const introCopy = computed(() =>
    prefill.source.length > 0
        ? `Prefilled from ${prefill.source}. Correct anything that looks wrong.`
        : returning
          ? "Check what I have, and correct anything that has changed."
          : "Quick intro — tell me who you are and I’ll take it from here.",
);

function handleSubmit(): void {
    if (!ready.value || busy || managesPeople.value === null) {
        return;
    }
    emit("submit", {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        role: values.role.trim(),
        managesPeople: managesPeople.value,
    });
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .heading.is-centred {
        @apply text-center;
    }
    .intro {
        @apply body mb-6 text-ink-muted;
    }
    .step.is-embedded .intro {
        @apply body-small;
    }
    .fields {
        @apply mt-6 grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2;
    }
    .field {
        @apply min-w-0;
    }
    .role-field {
        @apply sm:col-span-2;
    }
    .field-input {
        @apply body w-full border-0 border-b border-border-strong bg-transparent px-0 py-2 text-ink;
        @apply focus:border-ink focus:ring-0 focus:outline-none;
    }
    .manages {
        @apply mt-8;
    }
    .manages-legend {
        @apply body-small text-control-label;
    }
    .manages-options {
        @apply mt-3 grid gap-3 sm:grid-cols-2;
    }
    .option {
        @apply flex cursor-pointer flex-col rounded-lgr border border-border bg-surface-raised p-4 text-left transition-colors hover:border-border-strong;
        @apply focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:outline-none;
    }
    .option.is-selected {
        @apply border-accent bg-accent-soft/30;
    }
    .option-head {
        @apply flex items-center gap-2;
    }
    .option-icon {
        @apply shrink-0 text-ink-muted;
    }
    .option.is-selected .option-icon,
    .option-check {
        @apply text-accent;
    }
    .option-check {
        @apply ml-auto;
    }
    .option-label {
        @apply body-small-bold text-ink;
    }
    .option-body {
        @apply body-small mt-1.5 text-ink-muted;
    }
}
</style>
